import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/api.service';

interface SkinCollabEvent {
  _id: string;
  username: string;
  accountCount: number;
  status: 'pending' | 'processing' | 'completed' | 'canceled';
  claimedAt?: string;
  claimedAccountCount?: number;
  pricingTitle?: string;
  pricingQuantity: number;
  pricingTierPrice: number;
  totalPrice: number;
  chargedAmount: number;
  refundedAmount?: number;
  unpaidAmount: number;
  successfulAccounts: number;
  failedAccounts: number;
  skinExplosionAccounts: number;
  createdAt: string;
}

type OutcomeType = 'success' | 'failed' | 'skin-explosion';

const isCanceledEventStatus = (status: string) => status === 'canceled';

interface OutcomeAccounts {
  type: OutcomeType;
  count: number;
  accounts: string;
}

interface SkinCollabPricing {
  _id: string;
  title?: string;
  totalAccounts: number;
  price: number;
  userPrice?: number;
  sortOrder?: number;
}

interface SkinCollabSystemSummary {
  totalAccounts: number;
  successfulAccounts: number;
  failedAccounts: number;
  skinExplosionAccounts: number;
  totalAmount: number;
}

interface SkinCollabAccountSearchMatch {
  eventId: string;
  status: string;
  createdAt: string;
  matchedUsernames: string[];
}

interface SkinCollabQuote {
  accountCount: number;
  pricingTitle?: string;
  pricingQuantity: number;
  pricingTierPrice: number;
  price: number;
  discountPercent: number;
  discountAmount: number;
  payableAmount: number;
}

const SkinCollabEventsPage: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
  const [accounts, setAccounts] = useState('');
  const [events, setEvents] = useState<SkinCollabEvent[]>([]);
  const [systemSummary, setSystemSummary] = useState<SkinCollabSystemSummary | null>(null);
  const [isStatsVisible, setIsStatsVisible] = useState(false);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SkinCollabAccountSearchMatch[] | null>(null);
  const [isSearchingAccounts, setIsSearchingAccounts] = useState(false);
  const [pricing, setPricing] = useState<SkinCollabPricing[]>([]);
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});
  const [quantityDrafts, setQuantityDrafts] = useState<Record<string, string>>({});
  const [titleDrafts, setTitleDrafts] = useState<Record<string, string>>({});
  const [userPriceDrafts, setUserPriceDrafts] = useState<Record<string, string>>({});
  const [newQuantity, setNewQuantity] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newUserPrice, setNewUserPrice] = useState('');
  const [quote, setQuote] = useState<SkinCollabQuote | null>(null);
  const [quoteError, setQuoteError] = useState('');
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [viewingOutcome, setViewingOutcome] = useState<{ eventId: string; data: OutcomeAccounts } | null>(null);
  const [loadingOutcome, setLoadingOutcome] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState('');
  const [busyActionId, setBusyActionId] = useState<string | null>(null);
  const [releaseFormEventId, setReleaseFormEventId] = useState<string | null>(null);
  const [releaseDrafts, setReleaseDrafts] = useState<Record<string, { successful: string; failed: string; skinExplosion: string }>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const result = await apiService.get<SkinCollabEvent[]>('/skin-collab-events');
      setEvents(result || []);
      const summary = await apiService.get<SkinCollabSystemSummary>('/skin-collab-events/system-outcome-summary');
      setSystemSummary(summary);
      if (isAdmin) {
        const tiers = await apiService.get<SkinCollabPricing[]>('/admin/skin-collab-events/pricing');
        setPricing(tiers || []);
        setPriceDrafts(Object.fromEntries((tiers || []).map((item) => [item._id, String(item.price)])));
        setUserPriceDrafts(Object.fromEntries((tiers || []).map((item) => [item._id, String(item.userPrice ?? item.price)])));
        setQuantityDrafts(Object.fromEntries((tiers || []).map((item) => [item._id, String(item.totalAccounts)])));
        setTitleDrafts(Object.fromEntries((tiers || []).map((item) => [item._id, item.title || ''])));
      }
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Không thể tải dữ liệu sự kiện.');
    }
  }, [isAdmin]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const timer = setInterval(() => {
      apiService.get<SkinCollabEvent[]>('/skin-collab-events')
        .then((result) => setEvents(result || []))
        .catch((requestError) => console.error('Could not refresh skin collaboration event status:', requestError));
    }, 15000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const lines = accounts.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    setQuote(null);
    setQuoteError('');
    if (!lines.length) {
      setQuoteLoading(false);
      return;
    }
    const usernames = new Set<string>();
    for (const line of lines) {
      const separator = line.indexOf('|');
      if (separator <= 0 || separator === line.length - 1) {
        setQuoteLoading(false);
        setQuoteError('Mỗi dòng cần đúng định dạng username|password để tính giá.');
        return;
      }
      const username = line.slice(0, separator).trim().toLowerCase();
      const password = line.slice(separator + 1).trim();
      if (!username || !password) {
        setQuoteLoading(false);
        setQuoteError('Username và password không được để trống.');
        return;
      }
      usernames.add(username);
    }
    if (!usernames.size || usernames.size > 7000) {
      setQuoteLoading(false);
      if (usernames.size > 7000) setQuoteError('Mỗi sự kiện tối đa 7.000 acc.');
      return;
    }

    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      setQuoteLoading(true);
      try {
        const result = await apiService.post<SkinCollabQuote>('/skin-collab-events/quote', { accountCount: usernames.size });
        if (!cancelled) setQuote(result);
      } catch (requestError: any) {
        if (!cancelled) setQuoteError(requestError.response?.data?.message || 'Không thể tính giá hiện tại.');
      } finally {
        if (!cancelled) setQuoteLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [accounts]);

  const savePrice = async (currentTotalAccounts: number, totalAccounts: number, title: string, priceValue: string, userPriceValue: string) => {
    setBusy(true); setError(''); setMessage('');
    try {
      await apiService.post('/admin/skin-collab-events/pricing', {
        currentTotalAccounts, totalAccounts, title, price: Number(priceValue), userPrice: Number(userPriceValue),
      });
      setMessage(`Đã lưu gói ${title || `${totalAccounts} acc`}.`);
      await load();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Không thể cập nhật giá.');
    } finally { setBusy(false); }
  };

  const createEvent = async () => {
    if (!accounts.trim()) { setError('Vui lòng nhập danh sách acc.'); return; }
    setBusy(true); setError(''); setMessage('');
    try {
      const created = await apiService.post<{ accountCount: number; payableAmount: number }>('/skin-collab-events', { accounts });
      setAccounts('');
      setMessage(`Đã tạo sự kiện ${created.accountCount.toLocaleString()} acc · ${created.payableAmount.toLocaleString()}đ.`);
      await load();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Không thể tạo sự kiện quay skin hợp tác. Vui lòng kiểm tra lại danh sách acc và số dư ví.');
    } finally { setBusy(false); }
  };

  const viewOutcome = async (item: SkinCollabEvent, type: OutcomeType) => {
    const key = `${item._id}:${type}`;
    if (viewingOutcome?.eventId === item._id && viewingOutcome.data.type === type) {
      setViewingOutcome(null);
      return;
    }
    setLoadingOutcome(key);
    setCopyMessage('');
    try {
      const data = await apiService.get<OutcomeAccounts>(`/skin-collab-events/${item._id}/accounts?type=${type}`);
      setViewingOutcome({ eventId: item._id, data });
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Không thể tải danh sách acc kết quả.');
    } finally { setLoadingOutcome(null); }
  };

  const searchAccounts = async () => {
    const query = searchQuery.trim();
    if (query.length < 2) {
      setError('Nhập ít nhất 2 ký tự tên đăng nhập để tìm acc.');
      return;
    }
    setIsSearchingAccounts(true); setError('');
    try {
      const result = await apiService.get<SkinCollabAccountSearchMatch[]>(`/skin-collab-events/search-account?q=${encodeURIComponent(query)}`);
      setSearchResults(result);
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Không thể tìm acc trong đơn quay skin.');
    } finally { setIsSearchingAccounts(false); }
  };

  const copyOutcome = async () => {
    if (!viewingOutcome?.data.accounts) return;
    try {
      await navigator.clipboard.writeText(viewingOutcome.data.accounts);
      setCopyMessage('Đã copy danh sách acc.');
    } catch {
      setCopyMessage('Không thể copy tự động. Hãy chọn và copy nội dung thủ công.');
    }
  };

  const releaseEvent = async (item: SkinCollabEvent) => {
    setBusyActionId(item._id); setError(''); setMessage('');
    try {
      const draft = releaseDrafts[item._id] || { successful: '', failed: '', skinExplosion: '' };
      const result = await apiService.patch<{ remainingAccountCount: number }>(`/admin/skin-collab-events/${item._id}/release`, {
        successfulAccounts: draft.successful,
        failedAccounts: draft.failed,
        skinExplosionAccounts: draft.skinExplosion,
      });
      setMessage(result.remainingAccountCount > 0
        ? `Đã release. Còn ${result.remainingAccountCount.toLocaleString('vi-VN')} acc được đưa về hàng đợi.`
        : 'Đã ghi nhận kết quả, không còn acc cần chạy.');
      setReleaseFormEventId(null);
      setReleaseDrafts((current) => ({ ...current, [item._id]: { successful: '', failed: '', skinExplosion: '' } }));
      setViewingOutcome(null);
      await load();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Không thể release sự kiện.');
    } finally { setBusyActionId(null); }
  };

  const cancelEvent = async (item: SkinCollabEvent) => {
    const confirmation = isAdmin
      ? 'Hủy sự kiện này? Nếu sự kiện đã bắt đầu xử lý, tiền sẽ không tự hoàn về ví.'
      : 'Hủy sự kiện đang chờ? Tiền đã trừ sẽ được hoàn về ví.';
    if (!window.confirm(confirmation)) return;
    setBusyActionId(item._id); setError(''); setMessage('');
    try {
      const result = await apiService.patch<{ refundedAmount?: number }>(`/skin-collab-events/${item._id}/cancel`, {});
      setMessage(result.refundedAmount ? `Đã hủy sự kiện và hoàn ${result.refundedAmount.toLocaleString()}đ về ví.` : 'Đã hủy sự kiện.');
      setViewingOutcome(null);
      await load();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Không thể hủy sự kiện.');
    } finally { setBusyActionId(null); }
  };

  const hideEvent = async (item: SkinCollabEvent) => {
    if (!window.confirm('Xóa sự kiện này khỏi danh sách của bạn? Dữ liệu vẫn được giữ trên hệ thống và không ảnh hưởng thống kê.')) return;
    setBusyActionId(item._id); setError(''); setMessage('');
    try {
      await apiService.patch(`/skin-collab-events/${item._id}/hide`, {});
      setMessage('Đã ẩn sự kiện khỏi danh sách của bạn.');
      setViewingOutcome(null);
      await load();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Không thể ẩn sự kiện.');
    } finally { setBusyActionId(null); }
  };

  const matchingEventIds = searchResults ? new Set(searchResults.map((item) => item.eventId)) : null;
  const visibleEvents = matchingEventIds ? events.filter((item) => matchingEventIds.has(item._id)) : events;

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-5">
        {isAdmin ? (
          <section className="space-y-3 rounded-2xl bg-white p-5 shadow-sm">
            <div><h2 className="text-lg font-semibold text-gray-900">Giá quay skin hợp tác</h2><p className="text-sm text-gray-600">Thiết lập theo gói số lượng acc như bảng giá order.</p></div>
            <div className="hidden grid-cols-[minmax(100px,1fr)_90px_110px_110px_auto] gap-2 text-xs font-semibold uppercase text-gray-500 sm:grid"><span>Tên gói</span><span>Số acc</span><span>Giá Partner/Admin</span><span>Giá User</span><span /></div>
            {pricing.map((item) => <div key={item._id} className="grid grid-cols-1 items-center gap-2 border-t py-3 sm:grid-cols-[minmax(100px,1fr)_90px_110px_110px_auto]">
              <input aria-label="Tên gói acc" value={titleDrafts[item._id] || ''} onChange={(event) => setTitleDrafts((current) => ({ ...current, [item._id]: event.target.value }))} className="rounded-lg border px-3 py-2" placeholder="Tên gói" />
              <input aria-label="Số lượng acc" value={quantityDrafts[item._id] || ''} onChange={(event) => setQuantityDrafts((current) => ({ ...current, [item._id]: event.target.value.replace(/\D/g, '') }))} className="rounded-lg border px-3 py-2" inputMode="numeric" />
              <input aria-label="Giá Partner/Admin" value={priceDrafts[item._id] || ''} onChange={(event) => setPriceDrafts((current) => ({ ...current, [item._id]: event.target.value.replace(/\D/g, '') }))} className="rounded-lg border px-3 py-2" inputMode="numeric" />
              <input aria-label="Giá User" value={userPriceDrafts[item._id] || ''} onChange={(event) => setUserPriceDrafts((current) => ({ ...current, [item._id]: event.target.value.replace(/\D/g, '') }))} className="rounded-lg border px-3 py-2" inputMode="numeric" />
              <button disabled={busy} onClick={() => void savePrice(item.totalAccounts, Number(quantityDrafts[item._id]), titleDrafts[item._id] || '', priceDrafts[item._id] || '0', userPriceDrafts[item._id] || '0')} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">Lưu</button>
            </div>)}
            {pricing.length === 0 && <p className="py-3 text-sm text-gray-500">Chưa có gói giá.</p>}
            <div className="grid grid-cols-1 gap-2 border-t pt-3 sm:grid-cols-[minmax(100px,1fr)_90px_110px_110px_auto]">
              <input value={newTitle} onChange={(event) => setNewTitle(event.target.value)} placeholder="Tên gói, ví dụ: 1 acc" className="rounded-lg border px-3 py-2" />
              <input value={newQuantity} onChange={(event) => setNewQuantity(event.target.value.replace(/\D/g, ''))} placeholder="Số acc" className="rounded-lg border px-3 py-2" inputMode="numeric" />
              <input value={newPrice} onChange={(event) => setNewPrice(event.target.value.replace(/\D/g, ''))} placeholder="Giá Partner/Admin" className="rounded-lg border px-3 py-2" inputMode="numeric" />
              <input value={newUserPrice} onChange={(event) => setNewUserPrice(event.target.value.replace(/\D/g, ''))} placeholder="Giá User" className="rounded-lg border px-3 py-2" inputMode="numeric" />
              <button disabled={busy} onClick={() => void savePrice(0, Number(newQuantity), newTitle, newPrice || '0', newUserPrice || newPrice || '0')} className="rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">Thêm gói</button>
            </div>
          </section>
        ) : (
          <section className="space-y-3 rounded-2xl bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Tạo sự kiện quay skin hợp tác</h2>
            <p className="text-sm text-gray-600">Nhập mỗi dòng theo định dạng username|password. Tổng tiền được tính theo số acc và giá hiện tại.</p>
            <p className="text-sm text-gray-600">Theo tỷ lệ admin đã mở thì khoảng 700 acc sẽ nổ 1 acc. Acc ttt lpass ae tự chịu nha.</p>
            <textarea value={accounts} onChange={(event) => setAccounts(event.target.value)} rows={10} spellCheck={false} className="w-full rounded-xl border border-gray-300 px-4 py-3 font-mono text-sm" placeholder={'username1|password1\nusername2|password2'} />
            {(quote || quoteLoading || quoteError) && <div className="rounded-xl bg-blue-50 px-4 py-3 text-sm text-blue-900">
              {quoteError ? <span className="text-red-700">{quoteError}</span> : quoteLoading ? 'Đang tính giá...' : quote && <div className="flex flex-wrap justify-between gap-2"><span>{quote.accountCount.toLocaleString()} acc · {quote.pricingTitle || `${quote.pricingQuantity} acc`}: {quote.pricingTierPrice.toLocaleString()}đ / {quote.pricingQuantity} = {(quote.pricingTierPrice / quote.pricingQuantity).toLocaleString('vi-VN', { maximumFractionDigits: 2 })}đ/acc</span><span>{quote.discountAmount > 0 ? <><span className="mr-2 text-gray-500 line-through">{quote.price.toLocaleString()}đ</span><strong>{quote.payableAmount.toLocaleString()}đ</strong></> : <strong>{quote.price.toLocaleString()}đ</strong>}</span></div>}
            </div>}
            <button disabled={busy || !accounts.trim()} onClick={() => void createEvent()} className="rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white disabled:opacity-50">{busy ? 'Đang xử lý...' : 'Tạo sự kiện và thanh toán'}</button>
          </section>
        )}

        {(error || message) && <div role={error ? 'alert' : 'status'} className={`rounded-xl px-4 py-3 text-sm ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{error || message}</div>}

        <section className="space-y-3 rounded-2xl bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-2"><div><h3 className="text-lg font-semibold text-gray-900">Sự kiện</h3><p className="text-sm text-gray-500">Xem kết quả và danh sách acc theo từng trạng thái.</p></div><div className="flex gap-2"><button type="button" onClick={() => { setIsSearchVisible((visible) => !visible); setSearchResults(null); setSearchQuery(''); }} aria-label={isSearchVisible ? 'Đóng tìm acc' : 'Tìm acc trong đơn'} aria-expanded={isSearchVisible} title={isSearchVisible ? 'Đóng tìm acc' : 'Tìm acc trong đơn'} className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50">{isSearchVisible ? <span aria-hidden="true" className="text-lg leading-none">×</span> : <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4"><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></svg>}</button><button type="button" onClick={() => setIsStatsVisible((visible) => !visible)} aria-label={isStatsVisible ? 'Ẩn thống kê quay skin' : 'Hiện thống kê quay skin'} aria-expanded={isStatsVisible} title={isStatsVisible ? 'Ẩn thống kê quay skin' : 'Hiện thống kê quay skin'} className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50">{isStatsVisible ? <span aria-hidden="true" className="text-lg leading-none">×</span> : <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4"><path d="M4 19V5" /><path d="M4 19h17" /><rect x="7" y="11" width="3" height="5" rx="0.5" /><rect x="12" y="8" width="3" height="8" rx="0.5" /><rect x="17" y="5" width="3" height="11" rx="0.5" /></svg>}</button><button onClick={() => void load()} className="rounded-lg border px-3 py-2 text-sm">Làm mới</button></div></div>
          {isSearchVisible && <div className="flex flex-col gap-2 sm:flex-row"><input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void searchAccounts(); }} placeholder="Nhập username acc cần tìm..." className="min-w-0 flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-500" /><button type="button" disabled={isSearchingAccounts} onClick={() => void searchAccounts()} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">{isSearchingAccounts ? 'Đang tìm...' : 'Tìm acc'}</button></div>}
          {searchResults && <p role="status" className="text-xs text-gray-500">Tìm thấy acc trong {searchResults.length.toLocaleString('vi-VN')} đơn quay skin.</p>}
          {isStatsVisible && <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-5">
            <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm"><p className="text-xs text-gray-500">Tổng acc quay skin</p><p className="mt-1 text-xl font-semibold text-gray-900">{systemSummary?.totalAccounts.toLocaleString('vi-VN') ?? 'Đang tải...'}</p></div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3 shadow-sm"><p className="text-xs text-emerald-700">Acc thành công</p><p className="mt-1 text-xl font-semibold text-emerald-800">{systemSummary?.successfulAccounts.toLocaleString('vi-VN') ?? 'Đang tải...'}</p></div>
            <div className="rounded-xl border border-rose-100 bg-rose-50 p-3 shadow-sm"><p className="text-xs text-rose-700">Acc thất bại</p><p className="mt-1 text-xl font-semibold text-rose-800">{systemSummary?.failedAccounts.toLocaleString('vi-VN') ?? 'Đang tải...'}</p></div>
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-3 shadow-sm"><p className="text-xs text-amber-700">Acc nổ skin</p><p className="mt-1 text-xl font-semibold text-amber-800">{systemSummary?.skinExplosionAccounts.toLocaleString('vi-VN') ?? 'Đang tải...'}</p></div>
            <div className="col-span-2 rounded-xl border border-blue-100 bg-blue-50 p-3 shadow-sm md:col-span-1"><p className="text-xs text-blue-700">Tổng tiền</p><p className="mt-1 text-xl font-semibold text-blue-800">{systemSummary ? `${systemSummary.totalAmount.toLocaleString('vi-VN')}đ` : 'Đang tải...'}</p></div>
          </div>}
          {visibleEvents.map((item) => {
            const refundedAmount = item.refundedAmount ?? 0;
            const netAmount = Math.max(0, (item.chargedAmount ?? item.totalPrice) - refundedAmount);
            const outcomes: { type: OutcomeType; label: string; count: number; color: string }[] = [
              { type: 'success', label: 'Acc thành công', count: item.successfulAccounts, color: 'emerald' },
              { type: 'failed', label: 'Acc thất bại', count: item.failedAccounts, color: 'rose' },
              { type: 'skin-explosion', label: 'Acc nổ skin', count: item.skinExplosionAccounts, color: 'amber' },
            ];
            const statusStyle = item.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : item.status === 'processing' ? 'bg-blue-100 text-blue-800' : isCanceledEventStatus(item.status) ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800';
            const canRelease = isAdmin && item.status === 'processing';
            const canCancel = isAdmin
              ? !isCanceledEventStatus(item.status)
              : item.status === 'pending';
            const estimatedAccountCount = item.claimedAccountCount ?? item.accountCount;
            const estimatedMinutes = Math.max(1, Math.ceil(estimatedAccountCount * 60 / 5000));
            const estimatedCompletion = item.claimedAt
              ? new Date(new Date(item.claimedAt).getTime() + estimatedMinutes * 60_000).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
              : null;
            return <article key={item._id} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="space-y-4 p-4 sm:p-5">
                {searchResults && searchResults.find((match) => match.eventId === item._id) && <p className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800">Acc khớp: {searchResults.find((match) => match.eventId === item._id)?.matchedUsernames.join(', ')}</p>}
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div><div className="flex flex-wrap items-center gap-2"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase ${statusStyle}`}>{item.status}</span>{isAdmin && <span className="text-sm font-medium text-gray-700">{item.username}</span>}</div><p className="mt-2 text-sm text-gray-500">{new Date(item.createdAt).toLocaleString()} · {item.pricingTitle || `${item.pricingQuantity} acc`}</p>{item.status === 'processing' && <p className="mt-1 text-xs font-medium text-blue-700">Ước tính xử lý: khoảng {estimatedMinutes} phút{estimatedCompletion ? ` · Hoàn tất khoảng ${estimatedCompletion}` : ''} (5.000 acc ≈ 60 phút)</p>}</div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">{item.accountCount.toLocaleString()} <span className="text-sm font-medium text-gray-500">acc</span></p>
                    <p className="text-sm font-semibold text-blue-700">Tiền đơn: {item.totalPrice.toLocaleString('vi-VN')}đ</p>
                    <p className="mt-1 text-sm font-semibold text-emerald-700">Đã hoàn ví: {refundedAmount.toLocaleString('vi-VN')}đ</p>
                    {refundedAmount > 0 && <p className="text-xs text-gray-600">Chi phí sau hoàn: {netAmount.toLocaleString('vi-VN')}đ</p>}
                  </div>
                </div>
                <div className="grid gap-2 sm:grid-cols-3">
                  {outcomes.map((outcome) => {
                    const style = outcome.color === 'emerald'
                      ? { card: 'border-emerald-100 bg-emerald-50', text: 'text-emerald-700', number: 'text-emerald-900' }
                      : outcome.color === 'rose'
                        ? { card: 'border-rose-100 bg-rose-50', text: 'text-rose-700', number: 'text-rose-900' }
                        : { card: 'border-amber-100 bg-amber-50', text: 'text-amber-700', number: 'text-amber-900' };
                    return <div key={outcome.type} className={`rounded-lg border p-3 ${style.card}`}>
                    <div className="flex items-center justify-between gap-2"><div><p className={`text-xs font-semibold ${style.text}`}>{outcome.label}</p><p className={`mt-1 text-xl font-bold ${style.number}`}>{outcome.count.toLocaleString()}</p></div>
                      <button type="button" disabled={outcome.count < 1 || loadingOutcome === `${item._id}:${outcome.type}`} onClick={() => void viewOutcome(item, outcome.type)} className="rounded-lg border border-white bg-white/80 px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm hover:bg-white disabled:cursor-not-allowed disabled:opacity-40">{loadingOutcome === `${item._id}:${outcome.type}` ? 'Đang tải...' : viewingOutcome?.eventId === item._id && viewingOutcome.data.type === outcome.type ? 'Đóng' : 'Xem acc'}</button>
                    </div>
                  </div>;
                  })}
                </div>
                {(canRelease || canCancel || !isAdmin) && <div className="flex flex-wrap justify-end gap-2">
                  {canRelease && <button type="button" disabled={busyActionId === item._id} title="Nhập kết quả đã chạy rồi release acc còn lại" onClick={() => setReleaseFormEventId((current) => current === item._id ? null : item._id)} className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 transition hover:bg-amber-100 disabled:cursor-wait disabled:opacity-50">{releaseFormEventId === item._id ? 'Đóng Release' : 'Release'}</button>}
                  {canCancel && <button type="button" disabled={busyActionId === item._id} aria-label="Hủy sự kiện" title="Hủy sự kiện" onClick={() => void cancelEvent(item)} className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-wait disabled:opacity-50">{busyActionId === item._id ? 'Đang xử lý...' : 'Hủy đơn'}</button>}
                  {!isAdmin && <button type="button" disabled={busyActionId === item._id} aria-label="Xóa sự kiện khỏi danh sách" title="Xóa sự kiện khỏi danh sách" onClick={() => void hideEvent(item)} className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-wait disabled:opacity-50">{busyActionId === item._id ? 'Đang xử lý...' : 'Xóa'}</button>}
                </div>}
                {canRelease && releaseFormEventId === item._id && <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/50 p-3">
                  <p className="text-xs text-gray-600">Nhập các acc đã chạy theo kết quả. Các ô có thể để trống; lần lấy đơn tiếp theo chỉ nhận các acc chưa được nhập ở đây.</p>
                  <div className="grid gap-3 md:grid-cols-3">
                    <label className="space-y-1 text-xs font-semibold text-emerald-700">Acc thành công<textarea value={releaseDrafts[item._id]?.successful || ''} onChange={(event) => setReleaseDrafts((current) => ({ ...current, [item._id]: { successful: event.target.value, failed: current[item._id]?.failed || '', skinExplosion: current[item._id]?.skinExplosion || '' } }))} rows={4} placeholder={'username|password'} className="w-full rounded-lg border border-emerald-200 bg-white p-2 font-mono text-xs font-normal text-gray-800" /></label>
                    <label className="space-y-1 text-xs font-semibold text-rose-700">Acc thất bại<textarea value={releaseDrafts[item._id]?.failed || ''} onChange={(event) => setReleaseDrafts((current) => ({ ...current, [item._id]: { successful: current[item._id]?.successful || '', failed: event.target.value, skinExplosion: current[item._id]?.skinExplosion || '' } }))} rows={4} placeholder={'username|password'} className="w-full rounded-lg border border-rose-200 bg-white p-2 font-mono text-xs font-normal text-gray-800" /></label>
                    <label className="space-y-1 text-xs font-semibold text-amber-700">Acc nổ skin<textarea value={releaseDrafts[item._id]?.skinExplosion || ''} onChange={(event) => setReleaseDrafts((current) => ({ ...current, [item._id]: { successful: current[item._id]?.successful || '', failed: current[item._id]?.failed || '', skinExplosion: event.target.value } }))} rows={4} placeholder={'username|password'} className="w-full rounded-lg border border-amber-200 bg-white p-2 font-mono text-xs font-normal text-gray-800" /></label>
                  </div>
                  <div className="flex justify-end"><button type="button" disabled={busyActionId === item._id} onClick={() => void releaseEvent(item)} className="rounded-lg bg-amber-600 px-4 py-2 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50">{busyActionId === item._id ? 'Đang release...' : 'Lưu kết quả và Release'}</button></div>
                </div>}
              </div>
              {viewingOutcome?.eventId === item._id && <div className="border-t bg-gray-50 p-4 sm:p-5"><div className="mb-3 flex flex-wrap items-center justify-between gap-2"><div><h4 className="font-semibold text-gray-900">{outcomes.find((outcome) => outcome.type === viewingOutcome.data.type)?.label} · {viewingOutcome.data.count.toLocaleString()} acc</h4><p className="text-xs text-gray-500">Thông tin tài khoản được giải mã khi xem.</p></div><button type="button" disabled={!viewingOutcome.data.accounts} onClick={() => void copyOutcome()} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40">Copy acc</button></div>
                {viewingOutcome.data.accounts ? <textarea readOnly value={viewingOutcome.data.accounts} rows={8} className="w-full rounded-lg border border-gray-300 bg-white p-3 font-mono text-sm" /> : <p className="rounded-lg border border-dashed border-gray-300 bg-white p-4 text-sm text-gray-600">Chưa có danh sách acc cho kết quả này. API báo kết quả cần gửi danh sách acc, không chỉ số lượng.</p>}
                {copyMessage && <p role="status" className="mt-2 text-sm text-green-700">{copyMessage}</p>}
              </div>}
            </article>;
          })}
          {visibleEvents.length === 0 && <p className="py-6 text-center text-sm text-gray-500">{searchResults ? 'Không tìm thấy acc trong các đơn quay skin.' : 'Chưa có sự kiện.'}</p>}
        </section>
      </div>
    </div>
  );
};

export default SkinCollabEventsPage;
